<template>
  <vx-card class="mt-5" no-shadow card-border>
    <div
      class="flex justify-between items-center cursor-pointer select-none"
      @click="togglePanel"
    >
      <h4 class="card-title mb-0">
        {{ $t('nav.auditTrail') }}
        <span v-if="loaded && audits.length" class="text-sm text-gray-500 font-normal ml-2">
          ({{ audits.length }})
        </span>
      </h4>
      <feather-icon
        :icon="panelOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
        svgClasses="w-5 h-5 stroke-current text-gray-600"
      />
    </div>

    <div v-show="panelOpen" class="mt-4">
      <p class="text-sm text-gray-500 mb-3">
        Immutable log of e-invoice validation, submission, and status changes for this document.
      </p>

      <div v-if="loading" class="flex justify-center py-6">
        <vs-spinner color="primary" />
      </div>

      <div v-else-if="loaded && audits.length === 0" class="text-sm text-gray-500 py-4">
        No audit activity recorded for this document yet.
      </div>

      <vs-table
        v-else-if="loaded"
        stripe
        :data="audits"
        noDataText="No audit activity found"
      >
        <template slot="thead">
          <vs-th>When</vs-th>
          <vs-th>Activity</vs-th>
          <vs-th>Type</vs-th>
          <vs-th>Actor</vs-th>
          <vs-th>Detail</vs-th>
        </template>
        <template slot-scope="{ data }">
          <tbody>
            <vs-tr v-for="(tr, i) in data" :key="tr.id || i" :data="tr">
              <vs-td>
                <p class="text-sm">{{ tr.createdAt | moment('DD MMM YYYY') }}</p>
                <p class="text-xs text-gray-500">{{ tr.createdAt | moment('HH:mm:ss') }}</p>
              </vs-td>
              <vs-td>
                <vs-chip :color="actionColor(tr.action)" class="product-order-status">
                  {{ formatAction(tr.action) }}
                </vs-chip>
              </vs-td>
              <vs-td>
                <span class="text-sm">{{ formatType(tr.type) }}</span>
              </vs-td>
              <vs-td>
                <span class="text-sm">{{ formatActor(tr.updatedBy) }}</span>
              </vs-td>
              <vs-td>
                <div class="flex items-center">
                  <span class="text-sm mr-2 truncate" style="max-width: 200px" :title="detailOf(tr)">
                    {{ detailOf(tr) }}
                  </span>
                  <feather-icon
                    icon="EyeIcon"
                    svgClasses="w-5 h-5 hover:text-primary stroke-current cursor-pointer flex-shrink-0"
                    title="View details"
                    @click.stop="openDetails(tr)"
                  />
                </div>
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>
    </div>

    <vs-popup title="Audit Event Details" :active.sync="showDetail">
      <div class="p-2">
        <div v-if="detailLoading" class="flex justify-center py-10">
          <vs-spinner color="primary" />
        </div>
        <template v-else-if="detailAudit">
          <div class="mb-4 grid grid-cols-2 gap-3 text-sm">
            <div><span class="text-gray-500">Activity:</span> {{ formatAction(detailAudit.action) }}</div>
            <div><span class="text-gray-500">When:</span> {{ detailAudit.createdAt | moment('DD MMM YYYY, HH:mm:ss') }}</div>
            <div><span class="text-gray-500">Actor:</span> {{ formatActor(detailAudit.updatedBy) }}</div>
            <div><span class="text-gray-500">Type:</span> {{ formatType(detailAudit.type) }}</div>
          </div>
          <div v-if="detailChanges && detailChanges.length" class="mb-4">
            <h5 class="font-semibold mb-2">Field Changes</h5>
            <vs-table :data="detailChanges" stripe>
              <template slot="thead">
                <vs-th>Field</vs-th>
                <vs-th>Before</vs-th>
                <vs-th>After</vs-th>
              </template>
              <template slot-scope="{ data }">
                <tbody>
                  <vs-tr v-for="(row, idx) in data" :key="'ch-' + idx">
                    <vs-td><span class="text-sm font-medium">{{ row.field }}</span></vs-td>
                    <vs-td><span class="text-sm text-danger break-all">{{ stringify(row.oldValue) }}</span></vs-td>
                    <vs-td><span class="text-sm text-success break-all">{{ stringify(row.newValue) }}</span></vs-td>
                  </vs-tr>
                </tbody>
              </template>
            </vs-table>
          </div>
        </template>
        <div v-else class="text-center py-8 text-gray-500">No details available</div>
      </div>
    </vs-popup>
  </vx-card>
</template>

<script>
export default {
  name: 'EInvoiceAuditTrailSection',
  props: {
    docNo: { type: String, required: true },
    docType: { type: String, default: '' },
  },
  data() {
    return {
      panelOpen: false,
      loaded: false,
      loading: false,
      audits: [],
      showDetail: false,
      detailLoading: false,
      detail: null,
    }
  },
  computed: {
    tenantId() {
      return this.$store.state.tenant?.tenant?.[0]?.tenantId
    },
    detailAudit() {
      return this.detail?.audit || null
    },
    detailChanges() {
      return this.detail?.changes || null
    },
  },
  methods: {
    togglePanel() {
      this.panelOpen = !this.panelOpen
      if (this.panelOpen && !this.loaded && !this.loading) {
        this.loadAudits()
      }
    },
    async loadAudits() {
      if (!this.docNo || !this.tenantId) return
      this.loading = true
      try {
        const params = { docNo: this.docNo, tenantId: this.tenantId, type: 'E_INVOICE' }
        if (this.docType) params.docType = this.docType
        const rows = await this.$store.dispatch('auditTrail/fetchByDocNo', params)
        this.audits = Array.isArray(rows) ? rows : rows?.data || []
        this.loaded = true
      } catch (e) {
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: e.response?.data?.message || e.message || 'Failed to load audit trail',
        })
        this.audits = []
        this.loaded = true
      } finally {
        this.loading = false
      }
    },
    async openDetails(tr) {
      this.showDetail = true
      this.detail = null
      this.detailLoading = true
      try {
        this.detail = await this.$store.dispatch('auditTrail/fetchDetails', tr.id)
      } catch (e) {
        this.detail = { audit: tr, changes: null }
      } finally {
        this.detailLoading = false
      }
    },
    stringify(value) {
      if (value === null || value === undefined) return '—'
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value)
        } catch (err) {
          return String(value)
        }
      }
      return String(value)
    },
    formatActor(updatedBy) {
      const systemId = '00000000-0000-0000-0000-000000000000'
      if (!updatedBy || updatedBy === systemId || updatedBy === 'SYSTEM') {
        return 'SYSTEM'
      }
      return updatedBy
    },
    detailOf(tr) {
      if (tr.metadata && tr.metadata.reason) return tr.metadata.reason
      if (tr.businessProcessName) return tr.businessProcessName
      return '—'
    },
    formatAction(action) {
      if (!action) return '—'
      return String(action).replace(/_/g, ' ')
    },
    formatType(type) {
      if (!type) return '—'
      return String(type).replace(/_/g, ' ')
    },
    actionColor(action) {
      const a = String(action || '').toUpperCase()
      if (['CREATED', 'VALIDATED', 'SUBMITTED'].includes(a)) return 'success'
      if (['DELETED', 'VALIDATION_FAILED', 'CANCELLED'].includes(a)) return 'danger'
      if (['HELD', 'UPDATED'].includes(a)) return 'warning'
      return 'primary'
    },
  },
}
</script>
